import { cloneElement, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDown,
  ArrowUpRight,
  Bathtub,
  CaretDoubleRight,
  CaretLeft,
  CaretRight,
  Check,
  Cube,
  EnvelopeSimple,
  Fire,
  ForkKnife,
  Handshake,
  Leaf,
  MapPin,
  Medal,
  Phone,
  Plus,
  Ruler,
  Stack,
  Sun,
  Wrench,
} from "@phosphor-icons/react";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { BeforeAfter } from "@/components/site/BeforeAfter";
import { Btn, Eyebrow, Testimonials } from "@/components/site/Blocks";
import { ArrowLink, CountUp, DragRow, Marquee, SplitText } from "@/components/site/Motion";
import { openContactPanel } from "@/components/site/ContactPanel";
import { QuoteDialog } from "@/components/site/QuoteDialog";
import LeadForm from "@/pages/LeadForm";
import { SITE_IMG, MATERIALS, SERVICES, STEPS, WHY, materialImg } from "@/lib/siteImages";
import { TitleBand } from "@/components/site/TitleBand";
import { siteImg, sectionOrder } from "@/lib/siteContent";
import { useReveal } from "@/lib/useReveal";
import { API } from "@/lib/api";
import { COLLECTIONS_MENU } from "@/lib/siteMenu";

const BRAND_LINKS = Object.fromEntries(
  (COLLECTIONS_MENU.find((g) => g.slug === "brands")?.items || []).map((b) => [
    b.label.trim().toLowerCase(),
    b.to,
  ]),
);

/* Nordic Home-04 rhythm: alternating light (cream) and dark sections */
const TONE = {
  dark: {
    sec: "s-dark-tex s-on-dark text-white",
    plain: "bg-[color:var(--s-dark)] text-white",
    ink: "text-white",
    mute: "text-white/60",
    line: "border-white/12",
    bgLine: "bg-white/12",
    cell: "bg-[color:var(--s-dark)]",
    fig: "bg-white/5",
    tag: "s-tag !text-white/70",
    eyebrow: true,
    arrow: true,
  },
  light: {
    sec: "bg-[color:var(--s-bg)] text-[color:var(--s-ink)]",
    plain: "bg-[color:var(--s-cream)] text-[color:var(--s-ink)]",
    ink: "text-[color:var(--s-ink)]",
    mute: "text-[color:var(--s-mute)]",
    line: "border-[color:var(--s-line)]",
    bgLine: "bg-[color:var(--s-line)]",
    cell: "bg-[color:var(--s-bg)]",
    fig: "bg-[color:var(--s-line)]",
    tag: "s-tag",
    eyebrow: false,
    arrow: false,
  },
};

const slug = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const paragraphs = (s) => (s || "").split(/\n+/).map((p) => p.trim()).filter(Boolean);
const stripTags = (s) => (s || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const asCards = (items, fallback) =>
  items?.length
    ? items
    : fallback.map((f) => ({ title: f.name || f.title, body: f.blurb || f.body, category: f.name, image: "" }));

const FILLER = [
  SITE_IMG.kitchenDark, SITE_IMG.bath2, SITE_IMG.outdoor2, SITE_IMG.fireplace,
  SITE_IMG.kitchen2, SITE_IMG.walls, SITE_IMG.warehouse, SITE_IMG.fabrication,
];
const ROOMS = ["Kitchens", "Baths", "Outdoor", "Fireplaces", "Commercial"];

const photo = (path, w = 0) => `${API}/public/showroom/photo/${path}${w ? `?w=${w}` : ""}`;

const heroVideo = (url) => {
  const u = (url || "").trim();
  if (!u) return null;
  const yt = u.match(/(?:youtu\.be\/|[?&]v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return { type: "youtube", id: yt[1] };
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(u)) return { type: "file", src: u };
  return null;
};

/* letter-spaced Nordic section header — boxed-eyebrow title band */
const Head = ({ micro, title, testId, t, className = "", index }) => (
  <TitleBand
    eyebrow={micro}
    heading={title}
    dark={t?.ink === "text-white"}
    className={className}
    headingTestId={testId}
    index={index}
  />
);

/* NestEdge section index tag — "(0X) LABEL" */
const Ix = ({ n, label, dark = false, className = "" }) =>
  n ? (
    <div className={`s-index ${dark ? "s-on-dark" : ""} ${className}`} data-testid="site-section-index">
      <b>({String(n).padStart(2, "0")})</b>
      {label ? <span>{label}</span> : null}
    </div>
  ) : null;

/* ─────────────────────────── hero (dark) ─────────────────────────── */
const Hero = ({ hero, shop, tags }) => {
  const video = heroVideo(hero.video_url);
  const img = siteImg(hero.image, SITE_IMG.hero);
  const title = hero.title_top || "Beautifully crafted stone surfaces";
  const accent = hero.title_accent ?? "for inspired kitchens and bathrooms";
  const scrollDown = () => {
    const el = document.querySelector("[data-testid='site-hero']")?.nextElementSibling;
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <section className="relative min-h-[100svh] overflow-hidden bg-[color:var(--s-dark)] text-white" data-testid="site-hero">
      <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover" data-testid="site-hero-image" />
      {video?.type === "youtube" && (
        <iframe
          title="Background video"
          data-testid="site-hero-video"
          className="pointer-events-none absolute left-1/2 top-1/2 h-[56.25vw] min-h-full w-[177.78vh] min-w-full -translate-x-1/2 -translate-y-1/2"
          src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&mute=1&loop=1&playlist=${video.id}&controls=0&rel=0&playsinline=1&modestbranding=1&iv_load_policy=3`}
          allow="autoplay; encrypted-media"
        />
      )}
      {video?.type === "file" && (
        <video data-testid="site-hero-video" className="absolute inset-0 h-full w-full object-cover" src={video.src} autoPlay muted loop playsInline />
      )}
      <span className="absolute inset-0 bg-gradient-to-b from-[#0f0e0c]/70 via-[#0f0e0c]/55 to-[#0f0e0c]/85" />

      <div className="s-container relative flex min-h-[100svh] flex-col justify-end pb-14 pt-40 lg:pb-20">
        <div className="grid gap-10 lg:grid-cols-[1.7fr_1fr] lg:items-end">
          <div>
            <SplitText text={hero.eyebrow || "Blend craft & stone for spaces that last"} className="s-eyebrow s-on-dark" testId="site-hero-eyebrow" />
            <h1 className="s-h s-h-hero mt-8 max-w-5xl text-white" data-testid="site-hero-title">
              <SplitText text={title} base={350} />
              {accent !== "" && (
                <>
                  {" "}
                  <SplitText text={accent} base={1100} className="text-white/45" />
                </>
              )}
            </h1>
          </div>
          <div className="lg:justify-self-end lg:pb-3">
            <Link to="/gallery" data-testid="hero-explore" aria-label="Explore our work" className="group relative flex h-36 w-36 items-center justify-center sm:h-40 sm:w-40">
              <svg viewBox="0 0 100 100" className="s-rotor absolute inset-0 h-full w-full text-white/75">
                <defs>
                  <path id="heroExploreRing" d="M50,50 m-37,0 a37,37 0 1,1 74,0 a37,37 0 1,1 -74,0" />
                </defs>
                <text fill="currentColor" style={{ fontSize: "8.2px", fontWeight: 600, letterSpacing: "0.22em", textTransform: "uppercase" }}>
                  <textPath href="#heroExploreRing" startOffset="0">EXPLORE OUR WORK • EXPLORE OUR WORK • </textPath>
                </text>
              </svg>
              <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/40 text-white transition-colors duration-300 group-hover:bg-[color:var(--s-gold-bright)] group-hover:text-[color:var(--s-ink)] sm:h-16 sm:w-16">
                <ArrowUpRight size={20} weight="bold" />
              </span>
            </Link>
          </div>
        </div>

        <div className="mt-12 h-px w-full bg-white/15" />

        <div className="mt-8 flex flex-col gap-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-xl text-sm leading-relaxed text-white/65 sm:text-base" data-testid="site-hero-body">{hero.body}</p>
          <div className="flex flex-wrap items-center gap-6">
            <Btn to="/contact" testId="hero-quote">{hero.primary_label || "Get a free quote"}</Btn>
            <ArrowLink to="/inventory" dark testId="hero-inventory">{hero.secondary_label || "See what's in stock"}</ArrowLink>
          </div>
        </div>
        {shop.phone && <span className="sr-only">{shop.phone}</span>}
      </div>

      <button type="button" onClick={scrollDown} data-testid="hero-scroll-hint" aria-label="Scroll down" className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-3 text-white/60 transition-colors duration-300 hover:text-white lg:flex">
        <ArrowDown size={16} weight="bold" className="s-bob" />
      </button>
    </section>
  );
};

/* ── NestEdge top strip — features finished projects; each tile links to its gallery item ── */
const TopStrip = ({ items = [], fallback = [] }) => {
  const projectTiles = items.slice(0, 4).map((g) => ({ id: g.id, src: photo(g.thumb), label: g.title || g.color }));
  const used = new Set(projectTiles.map((t) => t.src));
  const need = 4 - projectTiles.length;
  const fillerTiles = need > 0
    ? fallback.filter((src) => src && !used.has(src)).slice(0, need).map((src) => ({ src }))
    : [];
  const tiles = [...projectTiles, ...fillerTiles].slice(0, 4);
  return (
    <div className="grid grid-cols-2 gap-px bg-[color:var(--s-dark)] md:grid-cols-4" data-testid="site-strip">
      {tiles.map((tile, i) => {
        const inner = (
          <>
            <img src={tile.src} alt={tile.label || ""} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
            {tile.label ? (
              <div className="pointer-events-none absolute inset-0 flex items-end bg-gradient-to-t from-black/75 via-black/5 to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <span className="s-micro flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white">
                  <span className="text-[color:var(--s-gold-bright)]">({String(i + 1).padStart(2, "0")})</span>
                  {tile.label}
                </span>
              </div>
            ) : null}
          </>
        );
        const cls = "group relative block h-28 overflow-hidden sm:h-36 md:h-44 lg:h-52";
        return tile.id ? (
          <Link key={i} to={`/gallery/${tile.id}`} className={cls} data-testid={`site-strip-${i}`} aria-label={tile.label ? `${tile.label} — see the project` : "See the project"}>
            {inner}
          </Link>
        ) : (
          <div key={i} className={cls} data-testid={`site-strip-${i}`}>{inner}</div>
        );
      })}
    </div>
  );
};

/* ───────────────────── introduction (light) ───────────────────── */
const Intro = ({ data, about, images, ticker, tone = "light", index }) => {
  const t = TONE[tone];
  if (data?.enabled === false) return null;
  const lead =
    data?.quote ||
    stripTags(about?.body) ||
    "Brioni Granite is committed to giving you the best stone in the region — the slab you pick is the slab we cut and set.";
  const body = paragraphs(data?.body || "");
  return (
    <section className={`${t.sec} s-secpad-t`} data-testid="site-founder-quote">
      <div className="s-container">
        <Head micro={data?.micro || "Introduction"} title={data?.heading || "Introduction."} testId="site-intro-heading" t={t} index={index} />
        <div className={`mt-12 h-px w-full ${t.bgLine}`} />
        <div className="grid gap-12 py-14 lg:grid-cols-2 lg:gap-20 lg:py-20">
          <div className="s-reveal">
            <p className={`text-2xl font-light leading-snug ${t.ink} sm:text-3xl`} data-testid="site-intro-lead">{lead}</p>
            {(data?.author || data?.image) && (
              <div className="mt-10 flex items-center gap-4" data-testid="site-founder-author">
                <img src={siteImg(data?.image, SITE_IMG.fabrication)} alt={data?.author || ""} className="h-14 w-14 rounded-full object-cover grayscale" data-testid="site-founder-image" />
                <div>
                  <div className={`text-sm font-semibold ${t.ink}`}>{data?.author}</div>
                  <div className={`s-micro mt-1 ${t.mute}`}>{data?.role}</div>
                </div>
              </div>
            )}
          </div>
          <div className="s-reveal" data-delay="1">
            {body.map((p, i) => (
              <p key={i} className={`mb-6 text-base leading-relaxed ${t.mute}`}>{p}</p>
            ))}
            <ArrowLink to="/about" dark={t.arrow} testId="site-founder-cta" className="mt-4">{data?.cta_label || "See more about the shop"}</ArrowLink>
          </div>
        </div>
      </div>

      <Marquee speed={55} testId="site-image-marquee" className="s-reveal">
        {images.map((src, i) => (
          <img key={i} src={src} alt="" loading="lazy" className="mr-4 h-56 w-[19rem] shrink-0 object-cover grayscale-[45%] transition-[filter] duration-500 hover:grayscale-0 sm:h-72 sm:w-[24rem]" />
        ))}
      </Marquee>

      <div className={`mt-6 border-y ${t.line} py-5`}>
        <Marquee speed={30} testId="site-ticker">
          {ticker.map((tg, i) => (
            <span key={i} className={t.tag}>{tg}</span>
          ))}
        </Marquee>
      </div>
    </section>
  );
};

/* ───────────────────────────── counters (dark) ───────────────────────────── */
const Counters = ({ band, stats, tone = "dark" }) => {
  const t = TONE[tone];
  if (band.enabled === false) return null;
  const rows = [
    band.show_slabs !== false && { n: stats.slabs ?? 0, unit: "Slabs", label: "Standing in our racks", to: "/inventory", cta: "Browse inventory" },
    band.show_colors !== false && { n: stats.colors ?? 0, unit: "Colors", label: "On the floor right now", to: "/inventory", cta: "See every color" },
    band.show_remnants !== false && { n: stats.remnants ?? 0, unit: "Remnants", label: "Priced to move", to: "/inventory?view=remnants", cta: "Remnant deals" },
    band.show_rating !== false && { n: Number(band.reviews) || 0, unit: "Reviews", label: `${band.rating || "4.8"}★ average on Google`, to: "/about", cta: "Read reviews" },
  ].filter(Boolean);
  if (rows.length === 0) return null;
  return (
    <section className={`${t.plain} s-secpad`} data-testid="site-stats-band">
      <div className={`s-reveal s-container grid gap-px overflow-hidden ${t.bgLine} sm:grid-cols-2 lg:grid-cols-4`} data-testid="site-stats">
        {rows.map((r) => (
          <div key={r.unit} className={`${t.cell} p-8 lg:p-10`} data-testid={`site-stat-${slug(r.unit)}`}>
            <div className={`s-num text-6xl leading-none ${t.ink} sm:text-7xl`}>
              <CountUp value={r.n} testId={`site-stat-value-${slug(r.unit)}`} />
              <span className="text-[color:var(--s-gold-bright)]">+</span>
            </div>
            <div className="s-micro mt-4 text-[color:var(--s-gold-bright)]">{r.unit}</div>
            <div className={`mt-6 text-base font-semibold ${t.ink}`}>{r.label}</div>
            <ArrowLink to={r.to} dark={t.arrow} className="mt-8 !text-[10px]" testId={`site-stat-link-${slug(r.unit)}`}>{r.cta}</ArrowLink>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ──────────────── creative approach cards (light) ──────────────── */
const APPROACH_ICONS = [Medal, Handshake, Ruler, Leaf];

const ApproachCard = ({ w, i }) => {
  const Icon = APPROACH_ICONS[i % APPROACH_ICONS.length] || Medal;
  return (
    <div
      data-testid={`site-why-row-${i}`}
      className="s-reveal flex flex-1 flex-col border border-[color:var(--s-line)] bg-[color:var(--s-cream)] p-8 lg:p-9"
      data-delay={String(i % 2)}
    >
      <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[color:var(--s-line)] text-[color:var(--s-ink)]">
        <Icon size={26} weight="light" />
      </span>
      <h3 className="s-hx mt-8 text-xl text-[color:var(--s-ink)] sm:text-2xl"><strong>{w.title}</strong></h3>
      <p className="mt-4 text-sm leading-relaxed text-[color:var(--s-mute)]">{w.body}</p>
    </div>
  );
};

const Approach = ({ cfg, items, tone = "light", index }) => {
  const t = TONE[tone];
  if (cfg?.enabled === false) return null;
  const cards = items.slice(0, 4);
  const img = siteImg(cfg?.image, SITE_IMG.bath2);
  return (
    <section className={`${t.sec} s-secpad`} data-testid="site-why">
      <div className="s-container">
        <Ix n={index} label="Our philosophy" dark={t.ink === "text-white"} className="mb-8" />
        <div className="grid gap-8 lg:grid-cols-[1.6fr_1fr] lg:items-end lg:gap-16" data-testid="site-why-titlebar">
          <h2 className={`s-reveal s-h text-4xl leading-[1.05] tracking-tight ${t.ink} sm:text-5xl lg:text-6xl`} data-testid="site-why-heading">
            {cfg?.heading ? (
              <strong>{cfg.heading}</strong>
            ) : (
              <strong>Creative approach crafting <span className={t.mute}>iconic</span> stone</strong>
            )}
          </h2>
          <div className="s-reveal lg:pb-2" data-delay="1">
            <p className={`text-base leading-relaxed ${t.mute}`} data-testid="site-why-eyebrow">
              {cfg?.sub || "We aim for beauty that lasts — a harmony between the stone, your space and the people who use it every day."}
            </p>
            <ArrowLink to="/about" dark={t.arrow} className="mt-6" testId="site-why-cta">Read more</ArrowLink>
          </div>
        </div>
        <div className="mt-14 grid gap-3 lg:grid-cols-3">
          <div className="flex flex-col gap-3">
            {cards.slice(0, 2).map((w, i) => (
              <ApproachCard key={w.title || i} w={w} i={i} />
            ))}
          </div>
          <div className="s-reveal s-fig relative min-h-[320px] overflow-hidden lg:min-h-full" data-testid="site-why-image">
            <img src={img} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
          </div>
          <div className="flex flex-col gap-3">
            {cards.slice(2, 4).map((w, i) => (
              <ApproachCard key={w.title || i} w={w} i={i + 2} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────── surfaces — big serif header + dark CTA + flush two-panel split (light) ─────────── */
const Surfaces = ({ cfg, materials, live, tone = "light", index }) => {
  const t = TONE[tone];
  if (cfg?.enabled === false) return null;
  const panels = materials.slice(0, 6);
  const ctaLabel =
    cfg?.micro && !["Featured surfaces", "Surfaces"].includes(cfg.micro) ? cfg.micro : "View all surfaces";
  return (
    <section className={`${t.plain} s-secpad-t`} data-testid="site-materials">
      <div className="s-container text-center">
        <div className="mb-6 flex justify-center"><Ix n={index} label="Material collections" dark={t.ink === "text-white"} /></div>
        <h2 className={`s-reveal s-h mx-auto max-w-4xl text-4xl leading-[1.06] ${t.ink} sm:text-5xl lg:text-6xl`} data-testid="site-materials-heading">
          {cfg?.heading || "Stone for every surface in your home."}
        </h2>
        <p className={`s-reveal mx-auto mt-6 max-w-2xl text-base leading-relaxed ${t.mute}`} data-delay="1" data-testid="site-materials-sub">
          {cfg?.sub || "Experience our surfaces in person — touch the slabs, ask questions and explore every possibility to create your ideal space."}
        </p>
        <div className="s-reveal mt-9 flex justify-center" data-delay="1">
          <Btn to="/inventory" variant="ghost" testId="materials-view-all">{ctaLabel}</Btn>
        </div>
      </div>
      <div className="mt-14 grid grid-cols-1 gap-0 sm:grid-cols-2" data-testid="site-material-panels">
        {panels.map((m) => {
          const cat = m.category || m.title;
          const hit = live.find((x) => (x.material || "").toLowerCase() === cat.toLowerCase());
          const img = siteImg(m.image, hit?.thumb ? photo(hit.thumb) : materialImg(m.title));
          return (
            <Link
              key={m.title}
              to={`/inventory?material=${encodeURIComponent(cat)}`}
              data-testid={`site-material-${slug(m.title)}`}
              className="group relative flex min-h-[360px] items-center justify-center overflow-hidden bg-[color:var(--s-dark)] lg:min-h-[540px]"
              draggable={false}
            >
              <img src={img} alt={m.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]" draggable={false} />
              <span className="absolute inset-0 bg-black/25 transition-colors duration-500 group-hover:bg-black/35" />
              <div className="relative flex flex-col items-center px-6 text-center">
                <h3 className="s-h text-3xl leading-tight text-white sm:text-4xl lg:text-5xl">{m.title}</h3>
                <span className="mt-7 inline-flex items-center gap-2 rounded-full bg-white/90 px-7 py-3 text-[11px] font-medium uppercase tracking-[0.16em] text-[#17171a] backdrop-blur-sm transition-colors duration-300 group-hover:bg-white">
                  Explore {m.title}
                  <CaretRight size={12} weight="bold" />
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

/* ───────────────────── featured projects grid (light) ───────────────────── */
const WorkGrid = ({ cfg, gallery, tone = "light", index }) => {
  const t = TONE[tone];
  if (cfg?.enabled === false) return null;
  const items = gallery.slice(0, 6);
  const total = gallery.length;
  const n = items.length;
  return (
    <section className={`${t.sec} s-secpad`} data-testid="site-gallery">
      <div className="s-container">
        <Ix n={index} label="Portfolio" dark={t.ink === "text-white"} className="mb-8" />
        <div className={`s-reveal flex flex-wrap items-end justify-between gap-6 border-b ${t.line} pb-8`} data-testid="site-gallery-titlebar">
          <h2 className={`s-h text-4xl uppercase leading-[0.95] tracking-tight ${t.ink} sm:text-6xl lg:text-7xl`} data-testid="site-gallery-heading">
            <strong>{cfg?.heading || "Featured projects"}</strong>
          </h2>
          <div className="flex items-center gap-5">
            <div className="text-right leading-none">
              <div className={`s-num text-2xl ${t.ink}`} data-testid="site-gallery-eyebrow">[{total}]</div>
              <div className={`s-micro mt-1 ${t.mute}`}>Projects</div>
            </div>
            <Link to="/gallery" aria-label="All projects" data-testid="gallery-view-all" className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[color:var(--s-ink)] text-white transition-transform duration-300 hover:scale-105 sm:h-20 sm:w-20">
              <ArrowUpRight size={22} weight="bold" />
            </Link>
          </div>
        </div>
        <p className={`s-reveal mt-6 max-w-xl text-base leading-relaxed ${t.mute}`} data-testid="site-gallery-sub">
          {cfg?.sub || "A look at kitchens, baths and more we've fabricated and installed."}
        </p>

        {n === 0 ? (
          <p className={`mt-12 text-center text-sm ${t.mute}`} data-testid="site-gallery-empty">
            Finished-job photos appear here as soon as they are published.
          </p>
        ) : (
          <div className="mt-12 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-testid="site-gallery-grid">
            {items.map((it, i) => (
              <Link
                key={it.id}
                to={`/gallery/${it.id}`}
                data-testid={`gallery-slide-${i}`}
                className="s-reveal group block"
                data-delay={String(i % 3)}
              >
                <div className="s-fig relative aspect-[4/5] overflow-hidden bg-[color:var(--s-dark)]">
                  <img src={photo(it.thumb, 900)} alt={it.title || it.color || "Project"} loading="lazy" className="h-full w-full object-cover" />
                  <span aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 scale-75 items-center justify-center rounded-full bg-[color:var(--s-ink)] text-[10px] font-semibold uppercase tracking-[0.2em] text-white opacity-0 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100">
                    Details
                  </span>
                  {it.before && <span className="s-badge s-badge-light absolute left-3 top-3">Before / after</span>}
                </div>
                <div className={`s-micro mt-5 !text-[10px] ${t.mute}`}>[ {(it.category || it.material || "Project").toUpperCase()} ]</div>
                <h3 className={`s-hx mt-2 text-xl ${t.ink} sm:text-2xl`}><strong>{it.title || it.color || "Finished project"}</strong></h3>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/* ─────────────── designs reflect you — icon cards + photo checklist (light) ─────────────── */
const REFLECT_ICONS = [Ruler, Stack, Wrench, Handshake];

const DesignsReflect = ({ cfg, tone = "light", index }) => {
  const t = TONE[tone];
  if (cfg?.enabled === false) return null;
  const cards = (cfg?.items || []).slice(0, 4);
  const checks = (cfg?.checklist || []).filter(Boolean);
  const img = siteImg(cfg?.image, SITE_IMG.kitchen2);
  if (cards.length === 0 && !cfg?.heading) return null;
  return (
    <section className={`${t.sec} s-secpad`} data-testid="site-reflect">
      <div className="s-container">
        <Ix n={index} label="Guarantee" dark={t.ink === "text-white"} className="mb-8" />
        <div className="grid gap-8 lg:grid-cols-[1.5fr_1fr] lg:items-end lg:gap-16" data-testid="site-reflect-titlebar">
          <h2 className={`s-reveal s-h text-4xl leading-[1.05] tracking-tight ${t.ink} sm:text-5xl lg:text-6xl`} data-testid="site-reflect-heading">
            {cfg?.heading ? (
              <strong>{cfg.heading}</strong>
            ) : (
              <strong>Designs that reflect <span className={t.mute}>who you are</span></strong>
            )}
          </h2>
          <div className="s-reveal lg:pb-2" data-delay="1">
            <p className={`text-base leading-relaxed ${t.mute}`} data-testid="site-reflect-sub">
              {cfg?.sub || "Every top is templated, laid out and cut for your space — not pulled off a shelf. You choose the slab, we match the veining and set it clean."}
            </p>
            <ArrowLink to="/services" dark={t.arrow} className="mt-6" testId="site-reflect-cta">Learn more</ArrowLink>
          </div>
        </div>
        <div className="mt-14 grid gap-4 lg:grid-cols-2 lg:gap-6">
          <div className="grid gap-4 sm:grid-cols-2" data-testid="site-reflect-grid">
            {cards.map((c, i) => {
              const Icon = REFLECT_ICONS[i % REFLECT_ICONS.length] || Cube;
              return (
                <div
                  key={c.title || i}
                  data-testid={`site-reflect-card-${i}`}
                  data-delay={String(i % 2)}
                  className={`s-reveal flex flex-col border ${t.line} bg-[color:var(--s-cream)] p-7 lg:p-8`}
                >
                  <span className={`flex h-14 w-14 items-center justify-center border ${t.line} ${t.ink}`}>
                    <Icon size={24} weight="light" />
                  </span>
                  <h3 className={`s-hx mt-6 text-lg ${t.ink} sm:text-xl`}><strong>{c.title}</strong></h3>
                  <p className={`mt-3 text-sm leading-relaxed ${t.mute}`}>{c.body}</p>
                </div>
              );
            })}
          </div>
          <div className="s-reveal relative min-h-[380px] overflow-hidden bg-[color:var(--s-dark)] lg:min-h-full" data-testid="site-reflect-image" data-delay="1">
            <img src={img} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            {checks.length > 0 && (
              <div className="absolute inset-x-0 bottom-0 bg-[color:var(--s-dark)]/92 p-7 backdrop-blur-sm sm:m-5 sm:p-8" data-testid="site-reflect-checklist">
                <ul className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                  {checks.slice(0, 6).map((c) => (
                    <li key={c} className="flex items-center gap-3 text-sm text-white/85">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color:var(--s-gold-bright)] text-[#17171a]">
                        <Check size={13} weight="bold" />
                      </span>
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ───────────────────── services accordion (dark) ───────────────────── */
const SERVICE_FILLER = [SITE_IMG.kitchen, SITE_IMG.bath, SITE_IMG.fireplace, SITE_IMG.outdoor, SITE_IMG.walls, SITE_IMG.polishing];
const SERVICE_ICONS = [ForkKnife, Bathtub, Fire, Sun, Stack, Wrench];

// pull short sub-service chips out of the service description (real, not invented)
const deriveTags = (body) =>
  (body || "")
    .split(/[,;]|\band\b/i)
    .map((s) => s.trim().replace(/[.]+$/, ""))
    .filter((s) => s.length > 1 && s.length < 34)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .slice(0, 6);

// Nordic "Services / What we offer" — dark showcase: left selector, right image + tag pills + detail box
const ServicesShowcase = ({ cfg, services, tone = "light", index }) => {
  const t = TONE[tone];
  const [active, setActive] = useState(0);
  const heading = cfg?.heading || "Services — what we offer";
  if (cfg?.enabled === false) return null;
  const items = services
    .filter((s) => {
      const tt = (s.title || "").toLowerCase();
      return !(tt.includes("repair") && tt.includes("polish"));
    })
    .slice(0, 6);
  if (items.length === 0) return null;
  const cur = items[active] || items[0];
  const Icon = SERVICE_ICONS[active % SERVICE_ICONS.length] || Cube;
  const img = siteImg(cur.image, SERVICE_FILLER[active % SERVICE_FILLER.length]);
  const tags = (Array.isArray(cur.tags) && cur.tags.length ? cur.tags : deriveTags(cur.body)).slice(0, 6);
  return (
    <section className={`${t.sec} s-secpad-t`} data-testid="site-services">
      <div className="s-container">
        <TitleBand
          eyebrow={cfg?.micro || "Our services"}
          heading={heading}
          tone={tone}
          align="right"
          index={index}
          bandTestId="site-services-titlebar"
          eyebrowTestId="site-services-eyebrow"
          headingTestId="site-services-heading"
        />
        <div className="s-reveal mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <p className={`max-w-xl text-base leading-relaxed ${t.mute}`} data-testid="site-services-sub">
            {cfg?.sub || "Templating, fabrication and installation — every surface cut and set by our own crew."}
          </p>
          <ArrowLink to="/services" dark={t.arrow} testId="services-all" className="shrink-0 !text-[10px]">
            All services
          </ArrowLink>
        </div>
      </div>

      <div
        className="s-reveal mt-8 grid overflow-hidden border-y border-white/10 bg-[color:var(--s-dark)] text-white lg:grid-cols-[minmax(320px,0.9fr)_1.7fr]"
        data-testid="site-services-grid"
      >
          {/* selector column */}
          <div className="flex flex-col border-b border-white/10 p-8 lg:border-b-0 lg:border-r lg:p-10">
            <div className="flex items-center gap-5">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-white/25 text-[color:var(--s-gold-bright)]">
                <Icon size={26} weight="light" />
              </span>
              <h3 className="s-hx text-2xl uppercase leading-tight sm:text-[2rem]" data-testid="site-service-active">
                <strong>{cur.title}</strong>
              </h3>
            </div>
            <div className="mt-10 flex flex-col gap-px lg:mt-auto lg:pt-12">
              {items.map((s, i) => {
                const on = i === active;
                return (
                  <button
                    key={s.title || i}
                    type="button"
                    onClick={() => setActive(i)}
                    onMouseEnter={() => setActive(i)}
                    aria-pressed={on}
                    data-testid={`site-service-card-${slug(s.title)}`}
                    className={`group flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors duration-300 ${
                      on ? "bg-white text-[#17171a]" : "bg-white/[0.04] text-white/85 hover:bg-white/[0.08] hover:text-white"
                    }`}
                  >
                    <span className="s-hx text-xl sm:text-2xl"><strong>{s.title}</strong></span>
                    <CaretDoubleRight size={20} weight="bold" className={on ? "text-[#17171a]" : "text-[color:var(--s-gold-bright)]"} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* preview column */}
          <div className="relative min-h-[560px] lg:min-h-[760px]">
            <img key={img} src={img} alt={cur.title} loading="lazy" className="s-fade absolute inset-0 h-full w-full object-cover" data-testid="site-service-image" />
            <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
            {tags.length > 0 && (
              <div className="absolute right-5 top-5 z-[1] flex max-w-[78%] flex-wrap justify-end gap-2.5" data-testid="site-service-tags">
                {tags.map((tg, i) => (
                  <span key={i} className="rounded-full border border-white/25 bg-black/45 px-4 py-1.5 text-xs font-medium text-white backdrop-blur-sm">
                    {tg}
                  </span>
                ))}
              </div>
            )}
            <div className="absolute bottom-0 right-0 z-[1] max-w-md bg-[color:var(--s-dark)]/92 p-7 backdrop-blur-sm sm:m-6 sm:p-8" data-testid="site-service-detail">
              <p className="text-sm leading-relaxed text-white/75">{cur.body}</p>
              <ArrowLink to="/services" dark className="mt-6 !text-[10px]" testId={`site-service-link-${slug(cur.title)}`}>Service details</ArrowLink>
            </div>
          </div>
        </div>
    </section>
  );
};

/* ───────────────── how we work — photo grid of the five steps (light) ───────────────── */
const WORKFLOW_ICONS = [Handshake, Stack, Ruler, Wrench, Medal];
const STEP_FILLER = [SITE_IMG.fabrication, SITE_IMG.warehouse, SITE_IMG.polishing, SITE_IMG.kitchen2, SITE_IMG.bath2];

const HowWeWork = ({ cfg, steps, tone = "light", index }) => {
  const t = TONE[tone];
  const [active, setActive] = useState(0);
  if (cfg?.enabled === false) return null;
  const items = steps.slice(0, 5);
  if (items.length === 0) return null;
  const num = (n) => String(n + 1).padStart(2, "0");
  return (
    <section className={`${t.sec} s-secpad`} data-testid="site-workflow">
      <div className="s-container">
        <TitleBand
          eyebrow={cfg?.micro || "How we work"}
          heading={cfg?.heading || "A clear, professional process"}
          tone={tone}
          align="right"
          index={index}
          bandTestId="site-workflow-titlebar"
          eyebrowTestId="site-workflow-eyebrow"
          headingTestId="site-workflow-heading"
        />
        <div className="s-reveal mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <p className={`max-w-xl text-base leading-relaxed ${t.mute}`} data-testid="site-workflow-sub">
            {cfg?.sub || "From the first call to the final polish — every step is handled by our own crew, so nothing is sub-contracted and nothing gets lost."}
          </p>
          <ArrowLink to="/about" dark={t.arrow} testId="site-workflow-all" className="shrink-0 !text-[10px]">How we work</ArrowLink>
        </div>

        <div
          className="s-reveal mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:mt-14 lg:flex lg:gap-4"
          data-testid="site-workflow-steps"
        >
          {items.map((s, i) => {
            const on = i === active;
            const Icon = WORKFLOW_ICONS[i % WORKFLOW_ICONS.length] || Cube;
            const img = siteImg(s.image, STEP_FILLER[i % STEP_FILLER.length]);
            const lastOdd = i === items.length - 1 && items.length % 2 === 1;
            return (
              <button
                key={s.title || i}
                type="button"
                onClick={() => setActive(i)}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                aria-pressed={on}
                data-testid={`site-workflow-step-${i}`}
                data-active={on ? "1" : "0"}
                className={`group relative min-h-[320px] overflow-hidden bg-[color:var(--s-dark)] text-left text-white transition-[flex-grow] duration-700 ease-[cubic-bezier(.2,.7,.2,1)] sm:min-h-[380px] lg:min-h-[560px] lg:basis-0 ${
                  on ? "lg:grow-[2.2]" : "lg:grow"
                }${lastOdd ? " sm:col-span-2" : ""}`}
              >
                <img
                  src={img}
                  alt={s.title}
                  loading="lazy"
                  className={`absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out ${on ? "scale-105" : "group-hover:scale-105"}`}
                />
                <span
                  aria-hidden
                  className={`absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10 transition-opacity duration-500 ${on ? "opacity-100" : "opacity-80"}`}
                />
                <span className={`s-num absolute left-5 top-5 text-3xl leading-none ${on ? "text-[color:var(--s-gold-bright)]" : "text-white/80"}`}>
                  {num(i)}
                </span>
                <span className="absolute right-5 top-5 flex h-12 w-12 items-center justify-center rounded-full border border-white/30 text-[color:var(--s-gold-bright)] backdrop-blur-sm">
                  <Icon size={20} weight="light" />
                </span>
                <span className="absolute inset-x-0 bottom-0 p-6 lg:p-7">
                  <span
                    className="s-hx block text-xl uppercase leading-tight sm:text-2xl"
                    data-testid={on ? "site-workflow-active" : undefined}
                  >
                    <strong>{s.title}</strong>
                  </span>
                  <span
                    className={`mt-3 block max-w-md text-sm leading-relaxed text-white/75 transition-all duration-500 lg:overflow-hidden ${
                      on ? "lg:max-h-40 lg:opacity-100" : "lg:max-h-0 lg:opacity-0"
                    }`}
                  >
                    {s.body}
                  </span>
                  <span
                    aria-hidden
                    className={`mt-4 hidden h-px bg-[color:var(--s-gold-bright)] transition-all duration-700 lg:block ${on ? "w-16" : "w-6"}`}
                  />
                </span>
              </button>
            );
          })}
        </div>

        <div className={`s-reveal mt-6 flex flex-col gap-5 border-t ${t.line} pt-8 sm:flex-row sm:items-center sm:justify-between`}>
          <p className={`text-sm ${t.mute}`}>A clear, professional process — delivering perfect results, every job.</p>
          <Btn to="/services" testId="site-workflow-learn-more">Learn more</Btn>
        </div>
      </div>
    </section>
  );
};

/* ───────────────────────────── location (dark) ───────────────────────────── */
const Location = ({ cfg, shop, contact, about, tone = "dark", index }) => {
  const t = TONE[tone];
  if (cfg?.enabled === false || !shop.address) return null;
  const hours = shop.hours_lines?.length ? shop.hours_lines : (contact?.hours || []).filter(Boolean);
  const maps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shop.address)}`;
  const invert = tone === "dark" ? "grayscale contrast-[1.1] invert-[0.92] hue-rotate-180" : "grayscale-[0.2]";
  return (
    <section className={`${t.plain} s-secpad`} data-testid="site-location">
      <div className="s-container grid gap-14 lg:grid-cols-[1fr_1.15fr] lg:gap-20">
        <div className="s-reveal">
          <TitleBand
            eyebrow={cfg?.micro || "Visit us"}
            heading={cfg?.heading || "Prime location."}
            tone={tone}
            align="left"
            index={index}
            headingTestId="site-location-heading"
            bandTestId="site-location-titlebar"
            eyebrowTestId="site-location-eyebrow"
          />
          <h3 className={`mt-10 whitespace-pre-line text-xl font-semibold leading-snug ${t.ink} sm:text-2xl`} data-testid="site-location-address">{shop.address}</h3>
          <p className={`mt-6 max-w-md text-base leading-relaxed ${t.mute}`}>{cfg?.body}</p>
          <ArrowLink href={maps} dark={t.arrow} className="mt-10" testId="site-location-directions">{cfg?.cta_label || "Get directions"}</ArrowLink>
          {hours.length > 0 && (
            <ul className={`mt-12 border-t ${t.line}`} data-testid="site-location-hours">
              {hours.map((h) => (
                <li key={h} className={`flex items-center justify-between border-b ${t.line} py-4 text-sm`}>
                  <span className={t.mute}>{h.split(/\s(?=\d)/)[0]}</span>
                  <span className={`font-semibold ${t.ink}`}>{h.replace(/^[^\d]*/, "") || "—"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className={`s-reveal s-soft relative aspect-[4/3] ${t.fig} lg:aspect-auto lg:min-h-[560px]`} data-delay="1">
          {about?.showroom_map !== false ? (
            <iframe
              title="Showroom map"
              data-testid="site-location-map"
              src={`https://www.google.com/maps?q=${encodeURIComponent(shop.address)}&output=embed`}
              className={`absolute inset-0 h-full w-full ${invert}`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <img src={SITE_IMG.warehouse} alt="" className="absolute inset-0 h-full w-full object-cover" />
          )}
          <span className="pointer-events-none absolute left-6 top-6 rounded-full bg-white px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#17171a]">
            {shop.name || "Brioni Granite"}
          </span>
        </div>
      </div>
    </section>
  );
};

/* ───────────────────────── contact / quote form (light) ───────────────────────── */
const ContactBlock = ({ cfg, shop, tone = "light", index }) => {
  const t = TONE[tone];
  if (cfg?.enabled === false) return null;
  return (
    <section className={`${t.sec} s-secpad`} data-testid="site-cta">
      <div className="s-container grid gap-14 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
        <div className="s-reveal">
          <TitleBand
            eyebrow={cfg?.micro || "Schedule a call"}
            heading={cfg?.heading || "Contact a consultant!"}
            tone={tone}
            align="left"
            index={index}
            headingTestId="site-cta-heading"
            bandTestId="site-cta-titlebar"
            eyebrowTestId="site-cta-eyebrow"
          />
          <p className={`mt-8 max-w-md text-base leading-relaxed ${t.mute}`}>{cfg?.body}</p>
          <ul className="mt-12 space-y-6">
            {shop.address && (
              <li className="flex items-start gap-4">
                <MapPin size={20} weight="light" className="mt-0.5 shrink-0 text-[color:var(--s-gold)]" />
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shop.address)}`} target="_blank" rel="noreferrer" className={`whitespace-pre-line text-base leading-relaxed ${t.ink} hover:text-[color:var(--s-gold)]`} data-testid="site-cta-address">
                  {shop.address}
                </a>
              </li>
            )}
            {shop.email && (
              <li className="flex items-start gap-4">
                <EnvelopeSimple size={20} weight="light" className="mt-0.5 shrink-0 text-[color:var(--s-gold)]" />
                <a href={`mailto:${shop.email}`} className={`break-all text-base ${t.ink} hover:text-[color:var(--s-gold)]`} data-testid="site-cta-email">{shop.email}</a>
              </li>
            )}
            {shop.phone && (
              <li className="flex items-start gap-4">
                <Phone size={20} weight="light" className="mt-0.5 shrink-0 text-[color:var(--s-gold)]" />
                <a href={`tel:${shop.phone_link || shop.phone}`} className={`text-base ${t.ink} hover:text-[color:var(--s-gold)]`} data-testid="cta-phone">{shop.phone}</a>
              </li>
            )}
          </ul>
          <ArrowLink to="/contact" dark={t.arrow} className="mt-12" testId="cta-quote">{cfg?.cta_label || "Full contact page"}</ArrowLink>
        </div>
        <div className="s-reveal border border-[color:var(--s-line)] bg-[color:var(--s-surface)] p-6 shadow-[0_40px_80px_-50px_rgba(0,0,0,0.35)] sm:p-10" data-delay="1" data-testid="site-cta-form">
          <h3 className="s-hx text-2xl text-[color:var(--s-ink)] sm:text-3xl">Send information to receive a quote.</h3>
          <div className="lead-embedded mt-6">
            <LeadForm embedded />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ───────────────────────────── brands (dark) ───────────────────────────── */
const LG_COLS = { 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5", 6: "lg:grid-cols-6" };
const LOGO_FILTER = { white: "brightness(0) invert(1)", black: "brightness(0)", original: "none" };

const Brands = ({ cfg, suppliers }) => {
  if (cfg?.enabled === false || suppliers.length === 0) return null;
  const cols = LG_COLS[Number(cfg?.columns)] || "lg:grid-cols-4";
  const logoKey = cfg?.logo_color || "white";
  const logoFilter = LOGO_FILTER[logoKey] || LOGO_FILTER.white;
  const textColor = logoKey === "black" ? "#1a1a18" : "#ffffff";
  const cell = "flex items-center justify-center overflow-hidden border-b border-r border-white/10";
  return (
    <section className="s-on-dark border-t border-white/10 bg-[color:var(--s-dark)] py-20 text-white" style={cfg?.bg_color ? { backgroundColor: cfg.bg_color } : undefined} data-testid="site-suppliers">
      <div className="s-container">
        <div className={`s-reveal grid grid-cols-2 border-l border-t border-white/10 sm:grid-cols-3 ${cols}`} data-testid="site-brands">
          <div className="col-span-2 flex flex-col justify-center gap-4 border-b border-r border-white/10 p-8 sm:col-span-3 lg:col-span-2 lg:p-12">
            <Eyebrow dark>{cfg?.micro || "Our client and partner"}</Eyebrow>
            <h2 className="s-h text-2xl uppercase sm:text-3xl lg:text-4xl" style={{ color: textColor }} data-testid="site-suppliers-heading">
              {cfg?.heading || "Our brands & partners."}
            </h2>
          </div>
          {suppliers.map((s) => {
            const href = BRAND_LINKS[(s.title || "").trim().toLowerCase()] || (cfg?.brand_pages !== false && s.slug ? `/brands/${s.slug}` : "/inventory");
            return (
              <Link key={s.title} to={href} data-testid={`site-brand-link-${slug(s.title)}`} className={`${cell} group h-32 p-6 opacity-80 transition-opacity duration-500 hover:opacity-100 sm:h-40`}>
                {s.image ? (
                  <img src={siteImg(s.image)} alt={s.title} style={{ filter: logoFilter }} className="max-h-12 w-auto max-w-full object-contain" data-testid={`site-brand-logo-${slug(s.title)}`} />
                ) : (
                  <span className="s-hx whitespace-nowrap text-2xl" style={{ color: textColor }} data-testid={`site-brand-${slug(s.title)}`}><strong>{s.title}</strong></span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ───────────────────────── slab carousel (light) ───────────────────────── */
const cleanSize = (s) => {
  if (!s) return "";
  const dims = String(s).match(/\d+(?:\.\d+)?/g) || [];
  return dims.length >= 2 && dims.some((d) => Number(d) === 0) ? "" : s;
};

const SlabCarousel = ({ cfg, featured, tone = "light", index }) => {
  const [hold, setHold] = useState(null);
  if (cfg?.enabled === false || featured.length === 0) return null;
  const tiles = featured.slice(0, 10);
  const holdMessage = (f) => {
    const size = cleanSize(f.size);
    return `Please put a hold on ${f.color}${size ? ` (${size}${f.thickness ? `, ${f.thickness}` : ""})` : f.thickness ? ` (${f.thickness})` : ""} for me.`;
  };
  return (
    <section className="s-dark-page bg-[color:var(--s-dark)] s-secpad" data-testid="site-featured">
      <div className="s-container text-center">
        <div className="mb-6 flex justify-center"><Ix n={index} label="Live inventory" dark /></div>
        <h2 className="s-reveal s-h mx-auto max-w-3xl text-4xl leading-[1.06] text-white sm:text-5xl lg:text-6xl" data-testid="site-featured-heading">
          {cfg?.heading || "New arrivals & sales"}
        </h2>
        <p className="s-reveal mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/60" data-delay="1" data-testid="site-featured-sub">
          {cfg?.sub || "Fresh slabs straight off the container and current deals — standing in our St. Louis warehouse now."}
        </p>
      </div>
      <div className="s-container mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 lg:gap-6" data-testid="site-featured-grid">
        {tiles.map((f) => (
          <div key={f.key} className="group relative" data-testid={`site-featured-${f.key}`}>
            <Link to={`/inventory/${f.key}`} className="block overflow-hidden bg-black" draggable={false}>
              <div className="s-fig relative aspect-[4/5]">
                {f.thumb ? (
                  <img src={photo(f.thumb, 700)} alt={f.color} className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" draggable={false} />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-white/50">Photo coming soon</div>
                )}
                <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <h3 className="s-h text-xl leading-tight text-white sm:text-2xl">{f.color}</h3>
                  {f.material && <span className="mt-1 block text-[11px] uppercase tracking-[0.14em] text-white/60">{f.material}</span>}
                </div>
              </div>
            </Link>
            <span className="pointer-events-none absolute right-3 top-3 flex flex-col items-end gap-2">
              {f.new_arrival && (
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[9px] font-semibold uppercase tracking-[0.08em] text-[#17171a]" data-testid={`site-featured-badge-new-${f.key}`}>New</span>
              )}
              {f.on_sale && (
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[color:var(--s-gold-bright)] text-[9px] font-semibold uppercase tracking-[0.08em] text-[#17171a]" data-testid={`site-featured-badge-sale-${f.key}`}>Sale</span>
              )}
            </span>
            <button type="button" onClick={() => setHold(f)} data-testid={`site-featured-hold-mobile-${f.key}`} className="absolute bottom-4 right-4 rounded-full bg-white/15 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.1em] text-white opacity-0 backdrop-blur-sm transition-opacity duration-300 hover:bg-white/25 group-hover:opacity-100">Hold</button>
          </div>
        ))}
      </div>
      <div className="s-container mt-12 flex justify-center">
        <ArrowLink to="/inventory" dark testId="featured-view-all">All inventory</ArrowLink>
      </div>
      <QuoteDialog open={!!hold} onClose={() => setHold(null)} color={hold?.color || ""} message={hold ? holdMessage(hold) : ""} source="Slab hold request" title={hold ? `Hold ${hold.color}` : ""} testId="site-hold-dialog" />
    </section>
  );
};

/* ───────────────────── 3D kitchen tour (dark) ───────────────────── */
const tourEmbed = (url) => {
  const u = (url || "").trim();
  if (!u) return null;
  const yt = u.match(/(?:youtu\.be\/|[?&]v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?rel=0` };
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(u)) return { type: "video", src: u };
  return { type: "iframe", src: u };
};

const KitchenTour = ({ cfg, tone = "dark", index }) => {
  const t = TONE[tone];
  const [i, setI] = useState(0);
  if (!cfg || cfg.enabled === false) return null;
  const embed = tourEmbed(cfg.embed_url);
  const slides = (cfg.slides || []).filter((s) => s.image);
  if (!embed && slides.length === 0) return null;
  const href = cfg.slab_key ? `/inventory/${cfg.slab_key}` : cfg.cta_href || "/inventory";
  const idx = slides.length ? ((i % slides.length) + slides.length) % slides.length : 0;
  return (
    <section className={`${t.plain} s-secpad`} data-testid="site-kitchen-tour">
      <div className="s-container grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:gap-16">
        <div>
          <Head micro={cfg.micro || "3D kitchen tour"} title={cfg.heading || "Step inside a quartzite kitchen"} testId="site-kitchen-tour-heading" t={t} spaced={false} index={index} />
          {cfg.sub && <p className={`s-reveal mt-6 max-w-xl text-base ${t.mute}`} data-testid="site-kitchen-tour-sub">{cfg.sub}</p>}
          <div className="s-reveal mt-8"><ArrowLink to={href} dark={t.arrow} testId="site-kitchen-tour-cta">{cfg.cta_label || "Explore this stone"}</ArrowLink></div>
        </div>
        <div className="s-reveal">
          <div className={`s-fig relative aspect-video overflow-hidden ${t.fig}`} data-testid="site-kitchen-tour-frame">
            {embed ? (
              embed.type === "video" ? (
                <video src={embed.src} controls playsInline className="h-full w-full object-cover" data-testid="site-kitchen-tour-video" />
              ) : (
                <iframe src={embed.src} title="3D kitchen tour" allow="fullscreen; accelerometer; gyroscope; xr-spatial-tracking; autoplay" allowFullScreen className="h-full w-full" data-testid="site-kitchen-tour-embed" />
              )
            ) : (
              <>
                <img key={slides[idx].image} src={siteImg(slides[idx].image)} alt={slides[idx].title || "Quartzite kitchen"} className="s-fade h-full w-full object-cover" data-testid="site-kitchen-tour-slide" />
                {slides[idx].title && <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-5 pb-4 pt-16 text-sm font-medium text-white">{slides[idx].title}</span>}
                {slides.length > 1 && (
                  <>
                    <button type="button" onClick={() => setI((n) => n - 1)} aria-label="Previous" data-testid="site-kitchen-tour-prev" className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#17171a] shadow transition-colors duration-200 hover:bg-white">
                      <CaretLeft size={16} weight="bold" />
                    </button>
                    <button type="button" onClick={() => setI((n) => n + 1)} aria-label="Next" data-testid="site-kitchen-tour-next" className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#17171a] shadow transition-colors duration-200 hover:bg-white">
                      <CaretRight size={16} weight="bold" />
                    </button>
                    <div className="absolute inset-x-0 bottom-3 flex justify-center gap-1.5">
                      {slides.map((s, n) => (
                        <button key={n} type="button" aria-label={`Slide ${n + 1}`} onClick={() => setI(n)} className={`h-1.5 rounded-full transition-all duration-300 ${n === idx ? "w-6 bg-white" : "w-1.5 bg-white/50"}`} />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ───────────────────────────── page ───────────────────────────── */
export default function SiteHome() {
  const site = useSite();
  const [gallery, setGallery] = useState([]);
  const shop = site?.shop || {};
  const stats = site?.stats || {};
  const cfg = site?.config || {};

  useEffect(() => {
    fetch(`${API}/public/gallery`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setGallery(d.items || []))
      .catch(() => setGallery([]));
  }, []);
  useReveal([site, gallery]);
  useEffect(() => {
    const a = cfg.appearance || {};
    if (a.theme === "dark" || a.bg) {
      const prev = document.body.style.backgroundColor;
      document.body.style.backgroundColor = a.bg || "#17171a";
      return () => {
        document.body.style.backgroundColor = prev;
      };
    }
  }, [cfg.appearance?.theme, cfg.appearance?.bg]);


  const hero = cfg.hero || {};
  const featured = (site?.featured || []).slice(0, Number(cfg.featured?.limit) || 10);
  const materials = asCards(cfg.materials?.items, MATERIALS);
  const services = asCards(cfg.services?.items, SERVICES);
  const why = asCards(cfg.why?.items, WHY);
  const steps = cfg.workflow?.items?.length ? cfg.workflow.items : STEPS;
  const suppliers = Object.values(site?.brand_logos || {});
  const marqueeImages = [
    ...gallery.map((g) => photo(g.thumb)),
    ...featured.filter((f) => f.thumb).map((f) => photo(f.thumb, 700)),
    ...FILLER,
  ].slice(0, 8);
  const heroTags = [
    ...(hero.eyebrow || "Minimal · Bespoke · Timeless · Crafted · Enduring").split("·").map((s) => s.trim()).filter(Boolean),
    "Granite", "Quartzite", "Marble", "Quartz", "Porcelain",
  ];
  const ticker = [
    ...(hero.eyebrow || "Supplier · Fabricator · Installer").split("·").map((s) => s.trim()).filter(Boolean),
    "St. Louis, Missouri", "Natural & engineered stone", "Free quote in 24 hours",
  ];

  const stripIds = cfg?.gallery?.strip_ids || [];
  const stripItems = stripIds.length
    ? stripIds.map((id) => gallery.find((g) => g.id === id)).filter(Boolean)
    : gallery;

  // Nordic Home-04 alternating light/dark rhythm (hero is dark)
  const blocks = {
    founder_quote: <Intro data={cfg.founder_quote} about={cfg.about} images={marqueeImages} ticker={ticker} tone="light" />,
    kitchen_tour: <KitchenTour cfg={cfg.kitchen_tour} tone="dark" />,
    why: <Approach cfg={cfg.why} items={why} tone="light" />,
    stats: <Counters band={cfg.stats || {}} stats={stats} tone="dark" />,
    materials: <Surfaces cfg={cfg.materials} materials={materials} live={site?.materials || []} tone="light" />,
    location: <Location cfg={cfg.location} shop={shop} contact={cfg.contact} about={cfg.about} tone="dark" />,
    gallery: <WorkGrid cfg={cfg.gallery} gallery={gallery} tone="light" />,
    reflect: <DesignsReflect cfg={cfg.reflect} tone="light" />,
    services: <ServicesShowcase cfg={cfg.services} services={services} tone="light" />,
    workflow: <HowWeWork cfg={cfg.workflow} steps={steps} tone="light" />,
    testimonials: <Testimonials data={cfg.testimonials} />,
    cta: <ContactBlock cfg={cfg.cta} shop={shop} tone="light" />,
    suppliers: <Brands cfg={cfg.suppliers} suppliers={suppliers} />,
    featured: <SlabCarousel cfg={cfg.featured} featured={featured} tone="light" />,
  };

  const appr = cfg.appearance || {};
  const spaceCls =
    appr.space === "compact" ? "s-space-compact" : appr.space === "roomy" ? "s-space-roomy" : "s-space-normal";
  const bgStyle = appr.bg ? { "--s-bg": appr.bg, "--s-cream": appr.bg } : undefined;
  const widths = appr.widths || {};

  return (
    <SiteShell site={site} testId="site-home" overHero wide>
      <Hero hero={hero} shop={shop} tags={heroTags} />
      <TopStrip items={stripItems} fallback={marqueeImages} />
      <div
        className={`bg-[color:var(--s-bg)] ${spaceCls}`}
        style={bgStyle}
        data-testid="site-home-body"
      >
        {sectionOrder(cfg)
          .filter((key) => blocks[key])
          .map((key, i) => (
            <div key={key} className={`s-sec${widths[key] === "full" ? " s-w-full" : ""}`}>
              {cloneElement(blocks[key], { index: i + 1 })}
            </div>
          ))}
      </div>
    </SiteShell>
  );
}
